import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { CELL_GAP_PX } from './columns'

export const TableCard = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.md};
  overflow: visible;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};

  @media (max-width: 560px) {
    flex-wrap: wrap;
  }
`

export const ToolbarCount = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px155}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
  white-space: nowrap;
`

export const ToolbarSpacer = styled.span`
  flex: 1;
`

export const PopoverAnchor = styled.div`
  position: relative;
`

export const ToolbarButton = styled.button<{ $active?: boolean; $highlight?: boolean }>`
  border: 1.5px solid
    ${({ theme, $active, $highlight }) =>
      $highlight
        ? theme.colors.primary
        : $active
        ? theme.colors.neutralGray
        : theme.colors.borderSoft};
  cursor: pointer;
  background: ${({ theme, $active, $highlight }) =>
    $highlight
      ? `color-mix(in srgb, ${theme.colors.primary} 8%, white)`
      : $active
      ? theme.colors.neutralGray
      : theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  font-family: inherit;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.px135}rem;
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  white-space: nowrap;
`

export const CountBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 800;
  min-width: 19px;
  height: 19px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
`

export const Panel = styled.div<{ $grow: 'left' | 'right'; $width: number }>`
  position: absolute;
  ${({ $grow }) => ($grow === 'right' ? 'left: 0;' : 'right: 0;')}
  top: calc(100% + 8px);
  width: ${({ $width }) => $width}px;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.card};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  z-index: 40;
  overflow: hidden;

  @media (max-width: 560px) {
    width: min(320px, 90vw);
  }
`

export const PanelHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.sm}rem;
`

export const PanelTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px145}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
`

export const PanelSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 500;
  margin-top: 2px;
`

export const PanelRowButton = styled.button`
  width: 100%;
  border: none;
  cursor: pointer;
  background: transparent;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  text-align: left;
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  /* Rings only for keyboard focus: the browser's default post-click
     outline hugged the row and the switch into an odd shape. */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

export const IconTile = styled.span<{ $bg: string; $color: string }>`
  width: 30px;
  height: 30px;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
`

export const PanelFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderSoft};
  background: ${({ theme }) => theme.colors.defaultHover};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

// Sticky block pinning the row's left padding + select checkbox + business
// cell during horizontal scroll. Inherits the row's background (including
// hover and urgency tints) so scrolling content disappears beneath it.
// $clickThrough (data rows only) lets clicks fall through to the RowLink
// overlay beneath it, except on the checkbox which re-enables its own.
// The gap mirrors the grid's column gap (CELL_GAP_PX feeds the min-width
// math in columns.tsx) so the pinned block and the grid stay aligned.
export const PinnedCell = styled.div<{ $clickThrough?: boolean }>`
  position: sticky;
  left: 0;
  z-index: 1;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: ${CELL_GAP_PX}px;
  padding-left: ${({ theme }) => theme.spacing.lg}rem;
  min-width: 0;
  background: inherit;
  transition: box-shadow 0.15s;
  ${({ $clickThrough }) => $clickThrough && 'pointer-events: none;'}
`

// $dimmed signals that the visible rows are a placeholder of the previous
// query while the next page/sort/filter is loading.
export const ScrollRegion = styled.div<{ $dimmed?: boolean }>`
  overflow-x: auto;
  overflow-y: visible;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
  transition: opacity 0.15s;

  &[data-scrolled='true'] ${PinnedCell} {
    box-shadow: 1px 0 0 ${({ theme }) => theme.colors.borderSoft};
  }

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.borderStrong};
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const gridRowBase = css<{ $template: string; $minWidth: number }>`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: center;
  gap: ${CELL_GAP_PX}px;
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.sm}rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

export const HeaderRowWrapper = styled.div<{ $template: string; $minWidth: number }>`
  ${gridRowBase};
  background: ${({ theme }) => theme.colors.defaultHover};
`

// A real button when sortable (keyboard operable); rendered as="div" for
// the non-sortable columns.
export const HeaderCell = styled.button<{ $sortable?: boolean }>`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.iconGray};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`

// The row itself is a plain grid; navigation happens through the RowLink
// overlay so the select checkbox is never a focusable descendant of a link
// (invalid nesting for assistive tech). focus-within keeps the hover tint
// when either the link or the checkbox has keyboard focus.
export const RowWrapper = styled.div<{
  $template: string
  $minWidth: number
  $bg: string
  $hoverBg: string
}>`
  ${gridRowBase};
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.md}rem 0;
  position: relative;
  background: ${({ $bg }) => $bg};

  &:hover,
  &:focus-within {
    background: ${({ $hoverBg }) => $hoverBg};
  }
`

// Absolutely positioned link covering the whole row: any cell without its
// own control clicks through to it, and it carries the row's accessible
// name for keyboard and screen-reader navigation.
export const RowLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

// Lives inside the sticky PinnedCell so it stays visible while scrolling;
// the negative offsets compensate half the row's vertical padding so the
// bar stops short of the row edges like the design.
export const AccentBar = styled.span<{ $color: string }>`
  position: absolute;
  left: 0;
  top: calc(${({ theme }) => theme.spacing.md}rem / -2);
  bottom: calc(${({ theme }) => theme.spacing.md}rem / -2);
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: ${({ $color }) => $color};
`

// Hosts the shared Checkbox inside a row: re-enables pointer events under
// the click-through PinnedCell and strips the input wrapper's form margin.
export const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  pointer-events: auto;

  /* Descendant selector: the checkbox's form margin must die even when a
     tooltip wrapper sits between it and this cell. */
  div {
    margin-bottom: 0;
  }
`

// Shared by the bulk-action bar and the coming-soon Responsible cells so
// every not-yet-functional surface looks the same.
export const ComingSoonPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  background: ${({ theme }) => theme.colors.mutedYellow};
  color: ${({ theme }) => theme.colors.menuBg};
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  white-space: nowrap;
`

export const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.xxl}rem ${({ theme }) => theme.spacing.lg}rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderSoft};
`

export const PaginationInfo = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 500;
`

export const PageButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const PageButton = styled.button<{
  $current?: boolean
  $disabled?: boolean
  $chevron?: boolean
}>`
  width: 32px;
  height: 32px;
  border: none;
  font-family: inherit;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $current }) =>
    $current ? theme.colors.primary : theme.colors.neutralGray};
  color: ${({ theme, $current, $disabled, $chevron }) =>
    $current
      ? theme.colors.neutralWhite
      : $disabled
      ? theme.colors.borderStrong
      : $chevron
      ? theme.colors.iconGray
      : theme.colors.metalGray};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $current }) => ($current ? 700 : 600)};
  cursor: ${({ $disabled, $current }) => ($disabled || $current ? 'default' : 'pointer')};
`
